/**
 * Facturación de la sociedad (Vistaláser, 7/10/2026)
 * ---------------------------------------------------
 * «Un módulo de facturación básico para una SL: facturas emitidas, gastos…
 * como el software básico que ya tenían integrado; con la inteligencia de
 * FactuAI». Cuatro pestañas:
 *
 *   · Resumen: lo facturado y los gastos del trimestre, las casillas
 *     orientativas del 303, el 111 y el 115, la prorrata y el Excel para la
 *     gestoría.
 *   · Facturas: borradores (se cambian y se borran) y emitidas (número
 *     correlativo; no se tocan: se rectifican o se anulan). Se preparan a
 *     mano, desde los cobros de los pacientes o desde las solicitudes de
 *     factura de recepción.
 *   · Gastos: las facturas recibidas, que se pueden leer con IA desde el PDF
 *     o la foto (si la clínica tiene la IA externa activada).
 *   · Ajustes: los datos fiscales de la sociedad que salen en cada factura.
 *
 * Las reglas las pone el servidor (routers/facturacion_router.py); las cuentas
 * de aquí (lib/facturacion.js) solo enseñan los importes mientras se escribe.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Landmark, FileText, Receipt, Settings2, Plus, Loader2, Download, Send, Trash2, Pencil,
  FileDown, Undo2, Ban, CheckCircle2, Sparkles, Upload, Paperclip, AlertTriangle, Users, Inbox,
} from 'lucide-react';
import { api, formatDate, formatEUR, mensajeError } from '../lib/api';
import { PageHeader, EmptyState, MiniStat, SectionCard } from '../components/Common';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { PatientCombobox } from '../components/PatientCombobox';
import {
  calcularFactura, calcularGasto, textoDeGrupo, num, trimestreDeHoy,
  CASILLAS_303, CASILLAS_111, CASILLAS_115,
} from '../lib/facturacion';

const SELECT = 'w-full h-10 px-3 rounded-md border border-input bg-background text-sm';
const TH = 'py-2.5 px-3 font-semibold text-[#13231E]';
const TD = 'py-2.5 px-3 align-top';

function Etiqueta({ children, tono = 'gris' }) {
  const tonos = {
    gris: 'bg-[rgba(222,222,222,0.55)] text-[rgba(11,18,32,0.78)] border-[rgba(46,110,94,0.14)]',
    verde: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    ambar: 'bg-amber-100 text-amber-900 border-amber-300',
    rojo: 'bg-red-100 text-red-900 border-red-300',
    azul: 'bg-sky-100 text-sky-900 border-sky-300',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${tonos[tono]}`}>
      {children}
    </span>
  );
}

function Periodo({ anio, setAnio, trimestre, setTrimestre }) {
  const actual = new Date().getFullYear();
  const anios = [actual + 1, actual, actual - 1, actual - 2];
  return (
    <div className="flex gap-2" data-testid="facturacion-periodo">
      <select className={`${SELECT} w-28`} value={anio} onChange={(e) => setAnio(Number(e.target.value))}
        aria-label="Año">
        {anios.map((a) => <option key={a} value={a}>{a}</option>)}
      </select>
      <select className={`${SELECT} w-36`} value={trimestre} onChange={(e) => setTrimestre(Number(e.target.value))}
        aria-label="Trimestre">
        <option value={0}>Año entero</option>
        {[1, 2, 3, 4].map((t) => <option key={t} value={t}>{t}.º trimestre</option>)}
      </select>
    </div>
  );
}

async function abrirBlob(url, nombre) {
  const r = await api.get(url, { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(r.data);
  const descargar = () => {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = nombre || 'documento';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  // Sin nombre se abre en otra pestaña; si el navegador la bloquea, se descarga.
  if (nombre || !window.open(blobUrl, '_blank')) descargar();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

function paramsDe(anio, trimestre, extra = {}) {
  const p = { anio, ...extra };
  if (trimestre) p.trimestre = trimestre;
  return p;
}

// ===========================================================================
// RESUMEN
// ===========================================================================
/** Las casillas de un modelo: euros, salvo los tipos y porcentajes
 *  (`porcentajes`) y los números de perceptores (`conteos`). */
function TablaCasillas({ casillas, nombres, porcentajes = [], conteos = [] }) {
  // Por número de casilla: un objeto JS pone «27» delante de «07» (las claves
  // que parecen enteros van primero), y el 303 se leía desordenado.
  const filas = Object.entries(casillas || {}).sort((a, b) => Number(a[0]) - Number(b[0]));
  if (!filas.length) return <p className="text-sm text-muted-foreground">Sin datos en el periodo.</p>;
  const valor = (k, v) => {
    if (porcentajes.includes(k)) return `${String(v).replace('.', ',')} %`;
    if (conteos.includes(k)) return v;
    return formatEUR(v);
  };
  return (
    <table className="w-full text-sm">
      <tbody>
        {filas.map(([k, v]) => (
          <tr key={k} className="border-b border-border last:border-0">
            <td className="py-1.5 pr-2 text-muted-foreground tabular-nums w-14">[{k}]</td>
            <td className="py-1.5 pr-2">{nombres[k] || ''}</td>
            <td className="py-1.5 text-right tabular-nums font-medium">{valor(k, v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TabResumen({ anio, trimestre }) {
  const [datos, setDatos] = useState(null);
  const [bajando, setBajando] = useState(false);

  useEffect(() => {
    setDatos(null);
    api.get('/api/facturacion/resumen', { params: paramsDe(anio, trimestre) })
      .then((r) => setDatos(r.data))
      .catch((e) => toast.error(mensajeError(e, 'No se ha podido calcular el resumen')));
  }, [anio, trimestre]);

  const excel = async () => {
    setBajando(true);
    try {
      await abrirBlob(`/api/facturacion/exportar?anio=${anio}${trimestre ? `&trimestre=${trimestre}` : ''}`,
        `facturacion-${trimestre ? `${trimestre}T-` : ''}${anio}.xlsx`);
    } catch (e) { toast.error(mensajeError(e, 'No se ha podido generar el Excel')); }
    finally { setBajando(false); }
  };

  if (!datos) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  const t = datos.totales || {};
  const c303 = datos.modelo_303?.casillas || {};
  const c111 = datos.modelo_111?.casillas || {};
  const c115 = datos.modelo_115?.casillas || {};
  const avisos = [...(datos.avisos || []), ...(datos.modelo_303?.avisos || [])];
  return (
    <div className="space-y-4" data-testid="facturacion-resumen">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniStat label="Facturado (base)" value={formatEUR(t.base_emitidas)} hint={`${t.facturas || 0} facturas · IVA ${formatEUR(t.iva_repercutido)}`} icon={FileText} />
        <MiniStat label="Gastos (base)" value={formatEUR(t.base_gastos)} hint={`${t.gastos || 0} gastos · IVA deducible ${formatEUR(t.iva_deducible)}`} icon={Receipt} />
        <MiniStat label="Resultado sin IVA" value={formatEUR(t.resultado)} hint="Facturado menos gastos" icon={Landmark} />
        <MiniStat label="Pendiente" value={formatEUR(t.pendiente_cobro)} hint={`de cobro · ${formatEUR(t.pendiente_pago)} de pago`} icon={Inbox} />
      </div>
      <div className="flex justify-end">
        <Button variant="outline" onClick={excel} disabled={bajando} data-testid="facturacion-excel">
          {bajando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Excel para la gestoría
        </Button>
      </div>
      {avisos.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-1">
          {avisos.map((a) => <div key={a} className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{a}</div>)}
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-4">
        <SectionCard title={`Modelo 303 · IVA ${datos.modelo_303?.resultado >= 0 ? 'a ingresar' : 'a compensar'}: ${formatEUR(datos.modelo_303?.resultado)}`}>
          <TablaCasillas casillas={c303} nombres={CASILLAS_303} porcentajes={['02', '05', '08', '151', '65']} />
        </SectionCard>
        <div className="space-y-4">
          <SectionCard title={`Modelo 111 · ${formatEUR(datos.modelo_111?.resultado)}`}>
            <TablaCasillas casillas={c111} nombres={CASILLAS_111} conteos={['01', '07']} />
          </SectionCard>
          <SectionCard title={`Modelo 115 · ${formatEUR(datos.modelo_115?.resultado)}`}>
            <TablaCasillas casillas={c115} nombres={CASILLAS_115} conteos={['01']} />
          </SectionCard>
          {datos.prorrata && (
            <SectionCard title={`Prorrata estimada: ${datos.prorrata.porcentaje} %`}>
              <p className="text-sm text-muted-foreground">
                Con lo facturado este año: {formatEUR(datos.prorrata.con_derecho)} con derecho a deducir y{' '}
                {formatEUR(datos.prorrata.sin_derecho)} exento por sanidad. Configurada: {datos.prorrata_configurada} %.
              </p>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// FACTURAS
// ===========================================================================
const LINEA_VACIA = { concepto: '', cantidad: 1, precio: '', descuento_pct: 0, iva_incluido: false, tipo_iva: 21, exenta: true, causa_exencion: 'E1' };

function ivaDeLinea(l) {
  return l.exenta ? 'EXENTA' : String(l.tipo_iva ?? 21);
}

function EditorFactura({ factura, ajustes, onClose, onGuardada }) {
  const rectificativa = factura?.clase === 'RECTIFICATIVA';
  const [form, setForm] = useState(() => ({
    clase: factura?.clase === 'SIMPLIFICADA' ? 'SIMPLIFICADA' : 'COMPLETA',
    patient_id: factura?.patient_id || null,
    destinatario: { nombre: '', tipo_documento: 'NIF', documento: '', domicilio: '', cp: '', municipio: '', provincia: '', pais: 'ES', email: '', ...(factura?.destinatario || {}) },
    lineas: (factura?.lineas?.length ? factura.lineas : [LINEA_VACIA]).map((l) => ({ ...LINEA_VACIA, ...l, causa_exencion: l.causa_exencion || 'E1' })),
    irpf_pct: factura?.irpf_pct || 0,
    fecha_operacion: factura?.fecha_operacion || '',
    notas: factura?.notas || '',
    forma_pago: factura?.forma_pago || '',
  }));
  const [guardando, setGuardando] = useState(false);
  const [confirmarEmision, setConfirmarEmision] = useState(false);
  const [problemas, setProblemas] = useState(factura?.para_emitir || []);
  const id = factura?.id;

  const setD = (k, v) => setForm((f) => ({ ...f, destinatario: { ...f.destinatario, [k]: v } }));
  const setLinea = (i, cambios) => setForm((f) => ({ ...f, lineas: f.lineas.map((l, j) => (j === i ? { ...l, ...cambios } : l)) }));
  const calc = useMemo(() => calcularFactura(form.lineas, form.irpf_pct), [form.lineas, form.irpf_pct]);

  const cuerpo = () => ({
    clase: form.clase,
    patient_id: form.patient_id,
    destinatario: form.destinatario,
    lineas: form.lineas.filter((l) => String(l.concepto).trim() || num(l.precio)).map((l) => ({
      concepto: String(l.concepto || '').trim() || 'Servicio',
      cantidad: l.cantidad === '' ? 1 : num(l.cantidad),
      precio: num(l.precio),
      descuento_pct: num(l.descuento_pct),
      iva_incluido: !!l.iva_incluido && !l.exenta,
      tipo_iva: l.exenta ? 0 : num(l.tipo_iva),
      exenta: !!l.exenta,
      causa_exencion: l.exenta ? (l.causa_exencion || 'E1') : null,
    })),
    irpf_pct: num(form.irpf_pct),
    fecha_operacion: form.fecha_operacion || null,
    notas: form.notas,
    forma_pago: form.forma_pago || null,
  });

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = id ? await api.put(`/api/facturacion/facturas/${id}`, cuerpo())
        : await api.post('/api/facturacion/facturas', cuerpo());
      setProblemas(r.data?.para_emitir || []);
      return r.data;
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar la factura'));
      return null;
    } finally { setGuardando(false); }
  };

  const guardarBorrador = async () => {
    const f = await guardar();
    if (f) { toast.success('Borrador guardado'); onGuardada(f); }
  };

  const emitir = async () => {
    setConfirmarEmision(false);
    const f = await guardar();
    if (!f) return;
    if (f.para_emitir?.length) {
      toast.error('Antes de emitir hay que completar lo que falta');
      onGuardada(f, true);
      return;
    }
    setGuardando(true);
    try {
      const r = await api.post(`/api/facturacion/facturas/${f.id}/emitir`);
      toast.success(`Factura ${r.data.numero_completo} emitida`);
      onGuardada(r.data);
      onClose();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido emitir'));
    } finally { setGuardando(false); }
  };

  const elegirPaciente = (pid, ficha) => {
    if (!ficha) { setForm((f) => ({ ...f, patient_id: null })); return; }
    const a = ficha.address || {};
    const nombre = [ficha.name, ficha.surname1, ficha.surname2].filter(Boolean).join(' ');
    setForm((f) => ({
      ...f,
      patient_id: pid,
      destinatario: {
        ...f.destinatario, nombre, documento: ficha.nif || f.destinatario.documento,
        domicilio: a.calle || '', cp: a.codigo_postal || '', municipio: a.poblacion || '',
        provincia: a.provincia || '', email: ficha.email || '',
        pais: !a.pais || /espa/i.test(a.pais) ? 'ES' : a.pais,
      },
    }));
  };

  const faltanEmisor = ajustes?.faltan || [];
  const titulo = rectificativa ? `Rectificativa de ${factura?.rectifica?.numero_completo}` : (id ? 'Borrador de factura' : 'Nueva factura');
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="editor-factura">
        <DialogHeader><DialogTitle>{titulo}</DialogTitle></DialogHeader>
        {rectificativa && (
          <div className="rounded-md bg-[#F0F6F2] border border-[#DDE7F2] p-3 text-sm">
            <b>{factura.rectifica.metodo === 'SUSTITUCION' ? 'Por sustitución' : 'Por diferencias'}</b> · {factura.tipo} · Motivo: {factura.rectifica.motivo}
            <div className="text-xs text-muted-foreground mt-1">
              {factura.rectifica.metodo === 'SUSTITUCION'
                ? 'Deja las líneas como deben quedar: la rectificativa sustituye a la original.'
                : 'Las líneas van en negativo: así anula la original entera. Ajusta las cantidades o los precios si solo hay que corregir una parte.'}
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-3">
          {!rectificativa && (
            <div>
              <Label>Tipo de factura</Label>
              <select className={SELECT} value={form.clase} onChange={(e) => setForm((f) => ({ ...f, clase: e.target.value }))} data-testid="factura-clase">
                <option value="COMPLETA">Completa (con datos del paciente)</option>
                <option value="SIMPLIFICADA">Simplificada (ticket, hasta 400 €)</option>
              </select>
            </div>
          )}
          <div className={rectificativa ? 'md:col-span-3' : 'md:col-span-2'}>
            <Label>Paciente (rellena los datos)</Label>
            <PatientCombobox value={form.patient_id} onChange={elegirPaciente} placeholder="Busca al paciente…" />
          </div>
        </div>
        <div className="grid md:grid-cols-6 gap-3">
          <div className="md:col-span-3"><Label>Nombre o razón social</Label><Input value={form.destinatario.nombre} onChange={(e) => setD('nombre', e.target.value)} data-testid="factura-nombre" /></div>
          <div className="md:col-span-1">
            <Label>Documento</Label>
            <select className={SELECT} value={form.destinatario.tipo_documento} onChange={(e) => setD('tipo_documento', e.target.value)}>
              <option value="NIF">NIF</option><option value="PASAPORTE">Pasaporte</option>
              <option value="NIF_IVA">NIF-IVA UE</option><option value="DOC_PAIS">Doc. país</option>
            </select>
          </div>
          <div className="md:col-span-2"><Label>Número</Label><Input value={form.destinatario.documento} onChange={(e) => setD('documento', e.target.value.toUpperCase())} data-testid="factura-documento" /></div>
          <div className="md:col-span-3"><Label>Domicilio</Label><Input value={form.destinatario.domicilio} onChange={(e) => setD('domicilio', e.target.value)} /></div>
          <div className="md:col-span-1"><Label>C.P.</Label><Input value={form.destinatario.cp} onChange={(e) => setD('cp', e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Municipio</Label><Input value={form.destinatario.municipio} onChange={(e) => setD('municipio', e.target.value)} /></div>
          <div className="md:col-span-3"><Label>Provincia</Label><Input value={form.destinatario.provincia} onChange={(e) => setD('provincia', e.target.value)} /></div>
          <div className="md:col-span-3"><Label>País</Label><Input value={form.destinatario.pais} onChange={(e) => setD('pais', e.target.value)} /></div>
        </div>

        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <Label>Líneas</Label>
            <span className="text-xs text-muted-foreground">«Precio con IVA» = el importe que pagó el paciente, con el IVA dentro</span>
          </div>
          <div className="space-y-2">
            {form.lineas.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`factura-linea-${i}`}>
                <div className="col-span-12 md:col-span-4"><Input placeholder="Concepto" value={l.concepto} onChange={(e) => setLinea(i, { concepto: e.target.value })} /></div>
                <div className="col-span-3 md:col-span-1"><Input aria-label="Cantidad" value={l.cantidad} onChange={(e) => setLinea(i, { cantidad: e.target.value })} /></div>
                <div className="col-span-4 md:col-span-2"><Input aria-label="Precio" placeholder="Precio €" value={l.precio} onChange={(e) => setLinea(i, { precio: e.target.value })} /></div>
                <div className="col-span-5 md:col-span-2">
                  <select className={SELECT} aria-label="IVA" value={ivaDeLinea(l)}
                    onChange={(e) => setLinea(i, e.target.value === 'EXENTA'
                      ? { exenta: true, causa_exencion: 'E1', tipo_iva: 0, iva_incluido: false }
                      : { exenta: false, causa_exencion: null, tipo_iva: Number(e.target.value) })}>
                    <option value="EXENTA">Exenta (sanidad)</option>
                    <option value="21">IVA 21 %</option><option value="10">IVA 10 %</option>
                    <option value="4">IVA 4 %</option><option value="0">IVA 0 %</option>
                  </select>
                </div>
                <label className="col-span-5 md:col-span-1 flex items-center gap-1 text-xs h-10">
                  <input type="checkbox" checked={!!l.iva_incluido} disabled={l.exenta || !num(l.tipo_iva)} onChange={(e) => setLinea(i, { iva_incluido: e.target.checked })} />
                  Precio con IVA
                </label>
                <div className="col-span-4 md:col-span-1"><Input aria-label="Descuento %" placeholder="Dto %" value={l.descuento_pct} onChange={(e) => setLinea(i, { descuento_pct: e.target.value })} /></div>
                <div className="col-span-3 md:col-span-1 flex justify-end">
                  <Button variant="ghost" size="icon" onClick={() => setForm((f) => ({ ...f, lineas: f.lineas.filter((_, j) => j !== i) }))} disabled={form.lineas.length === 1} aria-label="Quitar línea"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => setForm((f) => ({ ...f, lineas: [...f.lineas, { ...LINEA_VACIA }] }))}>
            <Plus className="w-4 h-4 mr-1" /> Añadir línea
          </Button>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Fecha de la operación (si no es hoy)</Label><Input type="date" value={form.fecha_operacion || ''} onChange={(e) => setForm((f) => ({ ...f, fecha_operacion: e.target.value }))} /></div>
          <div>
            <Label>Forma de pago</Label>
            <select className={SELECT} value={form.forma_pago || ''} onChange={(e) => setForm((f) => ({ ...f, forma_pago: e.target.value }))}>
              <option value="">—</option>
              {(ajustes?.catalogos?.formas_pago || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div><Label>Retención IRPF % (casi nunca en una SL)</Label><Input value={form.irpf_pct} onChange={(e) => setForm((f) => ({ ...f, irpf_pct: e.target.value }))} /></div>
          <div className="md:col-span-3"><Label>Descripción de la operación (sale en la factura)</Label><Textarea rows={2} value={form.notas} onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))} /></div>
        </div>

        <div className="rounded-lg border border-border p-3">
          <table className="w-full text-sm">
            <tbody>
              {calc.desglose.map((g) => (
                <tr key={`${g.exenta}-${g.tipo_iva}-${g.causa_exencion}`}>
                  <td className="py-0.5">{textoDeGrupo(g)}</td>
                  <td className="py-0.5 text-right tabular-nums">{formatEUR(g.base)}</td>
                  <td className="py-0.5 text-right tabular-nums w-28">{g.exenta ? '—' : formatEUR(g.cuota)}</td>
                </tr>
              ))}
              <tr className="border-t border-border font-semibold text-[#13231E]">
                <td className="pt-1.5">Total</td><td />
                <td className="pt-1.5 text-right tabular-nums text-base" data-testid="factura-total">{formatEUR(calc.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {(problemas.length > 0 || faltanEmisor.length > 0) && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="factura-problemas">
            <div className="font-medium mb-1">Para emitir falta:</div>
            <ul className="list-disc pl-5 space-y-0.5">{(problemas.length ? problemas : [`Datos fiscales de la sociedad: ${faltanEmisor.join(', ')} (pestaña Ajustes).`]).map((p) => <li key={p}>{p}</li>)}</ul>
          </div>
        )}
        {confirmarEmision ? (
          <div className="rounded-lg border border-[#2E6E5E]/40 bg-[#F0F6F2] p-3 text-sm" data-testid="factura-confirmar-emision">
            <div className="font-medium text-[#13231E]">¿Emitir la factura por {formatEUR(calc.total)}?</div>
            <p className="mt-1">Se le pone su número correlativo y la fecha de hoy. Una factura emitida ya no se modifica ni se
              borra: se corrige con una rectificativa.</p>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => setConfirmarEmision(false)} disabled={guardando}>Cancelar</Button>
              <Button onClick={emitir} disabled={guardando} data-testid="factura-emitir-confirmar">
                {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sí, emitir'}
              </Button>
            </div>
          </div>
        ) : (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose} disabled={guardando}>Cerrar</Button>
            <Button onClick={guardarBorrador} disabled={guardando} data-testid="factura-guardar">
              {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar borrador'}
            </Button>
            <Button onClick={() => setConfirmarEmision(true)} disabled={guardando} data-testid="factura-emitir">
              <Send className="w-4 h-4 mr-1" /> Emitir
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DialogoCobros({ anio, trimestre, onClose, onPreparada }) {
  const [cobros, setCobros] = useState(null);
  const [marcados, setMarcados] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/api/facturacion/cobros-sin-factura', { params: paramsDe(anio, trimestre, q ? { q } : {}) })
      .then((r) => setCobros(r.data || []))
      .catch((e) => { setCobros([]); toast.error(mensajeError(e, 'No se han podido leer los cobros')); });
  }, [anio, trimestre, q]);

  const persona = (c) => c.patient_id || c.patient_name;
  const elegidos = (cobros || []).filter((c) => marcados.includes(c.id));
  const de = elegidos.length ? persona(elegidos[0]) : null;

  const preparar = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/facturacion/facturas/desde-cobros', { cobro_ids: marcados });
      onPreparada(r.data);
    } catch (e) { toast.error(mensajeError(e, 'No se ha podido preparar la factura')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="dialogo-cobros">
        <DialogHeader><DialogTitle>Facturar cobros de pacientes</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Cobros del periodo que aún no tienen factura. Elige los de un mismo paciente: la factura sale con el importe que pagó.</p>
        <Input placeholder="Buscar paciente o concepto…" value={q} onChange={(e) => setQ(e.target.value)} />
        {cobros === null ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-6" /> : cobros.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No hay cobros sin factura en el periodo.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left"><tr><th className={TH} /><th className={TH}>Fecha</th><th className={TH}>Paciente</th><th className={TH}>Concepto</th><th className={`${TH} text-right`}>Importe</th></tr></thead>
            <tbody>
              {cobros.map((c) => {
                const bloqueado = (de && persona(c) !== de) || !!c.borrador_id;
                return (
                  <tr key={c.id} className="border-t border-border">
                    <td className={TD}><input type="checkbox" disabled={bloqueado} checked={marcados.includes(c.id)}
                      onChange={(e) => setMarcados((m) => (e.target.checked ? [...m, c.id] : m.filter((x) => x !== c.id)))} /></td>
                    <td className={TD}>{formatDate(c.fecha)}</td>
                    <td className={TD}>{c.patient_name}{c.borrador_id && <div className="text-[11px] text-amber-700">ya en un borrador</div>}</td>
                    <td className={TD}>{c.concepto}{c.con_iva ? <span className="text-xs text-muted-foreground"> · con IVA</span> : null}</td>
                    <td className={`${TD} text-right tabular-nums`}>{formatEUR(c.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button onClick={preparar} disabled={!marcados.length || busy} data-testid="cobros-preparar">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Preparar factura (${marcados.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogoSolicitudes({ onClose, onPreparada }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    api.get('/api/facturacion/solicitudes').then((r) => setItems(r.data || []))
      .catch((e) => { setItems([]); toast.error(mensajeError(e, 'No se han podido leer las solicitudes')); });
  }, []);
  const preparar = async (s) => {
    setBusy(s.id);
    try {
      const r = await api.post(`/api/facturacion/facturas/desde-solicitud/${s.id}`);
      onPreparada(r.data);
    } catch (e) { toast.error(mensajeError(e, 'No se ha podido preparar la factura')); }
    finally { setBusy(null); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="dialogo-solicitudes">
        <DialogHeader><DialogTitle>Solicitudes de factura de recepción</DialogTitle></DialogHeader>
        {items === null ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-6" /> : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No hay solicitudes pendientes.</p>
        ) : (
          <div className="divide-y divide-border">
            {items.map((s) => (
              <div key={s.id} className="py-2.5 flex items-start justify-between gap-3">
                <div className="text-sm">
                  <div className="font-medium">{s.patient_name} {s.importe ? <span className="text-muted-foreground">· {formatEUR(s.importe)}</span> : null}</div>
                  <div className="text-xs text-muted-foreground whitespace-pre-line">{s.datos_fiscales}</div>
                  <div className="text-[11px] text-muted-foreground">Apuntada el {formatDate(s.created_at)}{s.created_by_name ? ` por ${s.created_by_name}` : ''}</div>
                </div>
                <Button size="sm" onClick={() => preparar(s)} disabled={busy === s.id}>
                  {busy === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preparar factura'}
                </Button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogoRectificar({ factura, onClose, onPreparada }) {
  const simplificada = factura.clase === 'SIMPLIFICADA' || factura.tipo === 'R5';
  const [metodo, setMetodo] = useState('DIFERENCIAS');
  const [tipo, setTipo] = useState(simplificada ? 'R5' : 'R1');
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const preparar = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/facturacion/facturas/${factura.id}/rectificar`, { metodo, tipo, motivo: motivo.trim() });
      onPreparada(r.data);
    } catch (e) { toast.error(mensajeError(e, 'No se ha podido preparar la rectificativa')); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Rectificar {factura.numero_completo}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label>Cómo</Label>
            <select className={SELECT} value={metodo} onChange={(e) => setMetodo(e.target.value)}>
              <option value="DIFERENCIAS">Por diferencias (anular o corregir una parte)</option>
              <option value="SUSTITUCION">Por sustitución (la nueva sustituye a la original)</option>
            </select>
          </div>
          <div>
            <Label>Causa</Label>
            <select className={SELECT} value={tipo} onChange={(e) => setTipo(e.target.value)} disabled={simplificada}>
              {simplificada ? <option value="R5">Rectifica una factura simplificada</option> : (
                <>
                  <option value="R1">Error fundado en derecho, o descuento y devolución posteriores (art. 80.1 y 80.2)</option>
                  <option value="R2">El cliente está en concurso de acreedores (art. 80.3)</option>
                  <option value="R3">Crédito incobrable (art. 80.4)</option>
                  <option value="R4">Otro motivo</option>
                </>
              )}
            </select>
          </div>
          <div><Label>Motivo (sale en la factura)</Label><Textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej.: devolución de la señal; precio mal aplicado…" /></div>
          <p className="text-xs text-muted-foreground">Se prepara un BORRADOR para revisar; no cuenta hasta que se emite.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={preparar} disabled={busy || motivo.trim().length < 3}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preparar rectificativa'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogoAnular({ factura, onClose, onHecho }) {
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const anular = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/facturacion/facturas/${factura.id}/anular`, { motivo: motivo.trim() });
      toast.success(`Factura ${factura.numero_completo} anulada`);
      onHecho(r.data);
    } catch (e) { toast.error(mensajeError(e, 'No se ha podido anular')); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-destructive flex items-center gap-2"><Ban className="w-4 h-4" /> Anular {factura.numero_completo}</DialogTitle></DialogHeader>
        <p className="text-sm">Solo si la operación <b>no llegó a existir</b>. La factura no se borra y su número no se reutiliza; deja de contar en los impuestos. Para corregir importes, haz una rectificativa.</p>
        <div><Label>Motivo</Label><Textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="destructive" onClick={anular} disabled={busy || motivo.trim().length < 3}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Anular'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TabFacturas({ anio, trimestre, ajustes }) {
  const [items, setItems] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [q, setQ] = useState('');
  const [editando, setEditando] = useState(null);
  const [cobrosAbierto, setCobrosAbierto] = useState(false);
  const [solicitudesAbierto, setSolicitudesAbierto] = useState(false);
  const [rectificando, setRectificando] = useState(null);
  const [anulando, setAnulando] = useState(null);
  const [nSolicitudes, setNSolicitudes] = useState(0);

  const cargar = useCallback(() => {
    api.get('/api/facturacion/facturas', { params: paramsDe(anio, trimestre, { ...(filtro ? { estado: filtro } : {}), ...(q ? { q } : {}) }) })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { setItems([]); toast.error(mensajeError(e, 'No se han podido leer las facturas')); });
    api.get('/api/facturacion/solicitudes').then((r) => setNSolicitudes((r.data || []).length)).catch(() => {});
  }, [anio, trimestre, filtro, q]);
  useEffect(() => { cargar(); }, [cargar]);

  const abrirPreparada = (f) => {
    setCobrosAbierto(false); setSolicitudesAbierto(false); setRectificando(null);
    setEditando(f); cargar();
  };
  const borrar = async (f) => {
    try { await api.delete(`/api/facturacion/facturas/${f.id}`); toast.success('Borrador eliminado'); cargar(); }
    catch (e) { toast.error(mensajeError(e, 'No se ha podido borrar')); }
  };
  const cobrada = async (f, valor) => {
    try { await api.put(`/api/facturacion/facturas/${f.id}/cobro`, { cobrada: valor }); cargar(); }
    catch (e) { toast.error(mensajeError(e, 'No se ha podido anotar')); }
  };
  const pdf = async (f) => {
    try { await abrirBlob(`/api/facturacion/facturas/${f.id}/pdf`); }
    catch (e) { toast.error(mensajeError(e, 'No se ha podido generar el PDF')); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setEditando({})} data-testid="factura-nueva"><Plus className="w-4 h-4 mr-1" /> Nueva factura</Button>
          <Button variant="outline" onClick={() => setCobrosAbierto(true)} data-testid="factura-desde-cobros"><Receipt className="w-4 h-4 mr-1" /> Facturar cobros</Button>
          <Button variant="outline" onClick={() => setSolicitudesAbierto(true)} data-testid="factura-solicitudes">
            <Users className="w-4 h-4 mr-1" /> Solicitudes de recepción{nSolicitudes ? ` (${nSolicitudes})` : ''}
          </Button>
        </div>
        <div className="flex gap-2">
          <select className={`${SELECT} w-44`} value={filtro} onChange={(e) => setFiltro(e.target.value)} aria-label="Filtro">
            <option value="">Borradores y emitidas</option><option value="borradores">Solo borradores</option>
            <option value="emitidas">Solo emitidas</option><option value="pendientes_cobro">Pendientes de cobro</option>
          </select>
          <Input className="w-48" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      {items === null ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        : items.length === 0 ? (
          <EmptyState icon={FileText} title="Sin facturas en el periodo" description="Crea una a mano, desde los cobros de los pacientes o desde las solicitudes de recepción." />
        ) : (
          <div className="cp-card overflow-hidden"><div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tabla-facturas">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr><th className={TH}>Número</th><th className={TH}>Fecha</th><th className={TH}>Cliente</th>
                  <th className={`${TH} text-right`}>Base</th><th className={`${TH} text-right`}>IVA</th><th className={`${TH} text-right`}>Total</th>
                  <th className={TH}>Estado</th><th className={`${TH} text-right`}>Acciones</th></tr>
              </thead>
              <tbody>
                {items.map((f) => {
                  const borrador = f.estado === 'borrador';
                  return (
                    <tr key={f.id} className={`border-t border-border ${f.anulada ? 'opacity-60' : ''}`}>
                      <td className={`${TD} font-medium whitespace-nowrap`}>{f.numero_completo || <span className="text-muted-foreground">Borrador</span>}
                        {f.clase === 'RECTIFICATIVA' && <div className="text-[11px] text-muted-foreground">rectifica {f.rectifica?.numero_completo}</div>}</td>
                      <td className={`${TD} whitespace-nowrap`}>{f.fecha_expedicion ? formatDate(f.fecha_expedicion) : '—'}</td>
                      <td className={TD}>{f.destinatario?.nombre || <span className="text-muted-foreground">Sin destinatario</span>}</td>
                      <td className={`${TD} text-right tabular-nums`}>{formatEUR(f.base_imponible)}</td>
                      <td className={`${TD} text-right tabular-nums`}>{formatEUR(f.cuota_iva)}</td>
                      <td className={`${TD} text-right tabular-nums font-medium`}>{formatEUR(f.total)}</td>
                      <td className={TD}>
                        <div className="flex flex-wrap gap-1">
                          {borrador ? <Etiqueta>Borrador</Etiqueta> : f.anulada ? <Etiqueta tono="rojo">Anulada</Etiqueta> : <Etiqueta tono="azul">{f.clase === 'SIMPLIFICADA' ? 'Simplificada' : f.clase === 'RECTIFICATIVA' ? 'Rectificativa' : 'Emitida'}</Etiqueta>}
                          {!borrador && !f.anulada && (f.cobrada ? <Etiqueta tono="verde">Cobrada</Etiqueta> : <Etiqueta tono="ambar">Pendiente</Etiqueta>)}
                          {(f.rectificada_por || []).some((r) => !r.anulada) && <Etiqueta>Rectificada</Etiqueta>}
                        </div>
                      </td>
                      <td className={`${TD} text-right whitespace-nowrap`}>
                        <Button variant="ghost" size="icon" onClick={() => pdf(f)} title="PDF" aria-label="PDF"><FileDown className="w-4 h-4" /></Button>
                        {borrador ? (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => setEditando(f)} title="Editar" aria-label="Editar"><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => borrar(f)} title="Borrar borrador" aria-label="Borrar"><Trash2 className="w-4 h-4" /></Button>
                          </>
                        ) : !f.anulada && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => cobrada(f, !f.cobrada)} title={f.cobrada ? 'Marcar pendiente' : 'Marcar cobrada'} aria-label="Cobrada"><CheckCircle2 className={`w-4 h-4 ${f.cobrada ? 'text-emerald-600' : ''}`} /></Button>
                            <Button variant="ghost" size="icon" onClick={() => setRectificando(f)} title="Rectificar" aria-label="Rectificar"><Undo2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => setAnulando(f)} title="Anular" aria-label="Anular"><Ban className="w-4 h-4" /></Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div></div>
        )}
      {editando && <EditorFactura factura={editando.id ? editando : null} ajustes={ajustes} onClose={() => { setEditando(null); cargar(); }}
        onGuardada={(f, seguir) => { if (seguir || f.estado === 'borrador') setEditando(f); cargar(); }} />}
      {cobrosAbierto && <DialogoCobros anio={anio} trimestre={trimestre} onClose={() => setCobrosAbierto(false)} onPreparada={abrirPreparada} />}
      {solicitudesAbierto && <DialogoSolicitudes onClose={() => setSolicitudesAbierto(false)} onPreparada={abrirPreparada} />}
      {rectificando && <DialogoRectificar factura={rectificando} onClose={() => setRectificando(null)} onPreparada={abrirPreparada} />}
      {anulando && <DialogoAnular factura={anulando} onClose={() => setAnulando(null)} onHecho={() => { setAnulando(null); cargar(); }} />}
    </div>
  );
}

// ===========================================================================
// GASTOS
// ===========================================================================
function EditorGasto({ gasto, lectura, ajustes, onClose, onGuardado }) {
  const prorrata = ajustes?.prorrata_pct ?? 100;
  const categorias = ajustes?.catalogos?.categorias || [];
  const retenciones = ajustes?.catalogos?.retenciones || [];
  const base = lectura?.propuesta || gasto || {};
  const [form, setForm] = useState(() => ({
    proveedor: { nombre: '', nif: '', ...(base.proveedor || {}) },
    numero_factura: base.numero_factura || '',
    fecha: base.fecha || new Date().toISOString().slice(0, 10),
    fecha_recepcion: base.fecha_recepcion || '',
    categoria: base.categoria || 'OTROS',
    concepto: base.concepto || '',
    lineas: (base.lineas?.length ? base.lineas : [{ concepto: '', base: '', tipo_iva: 21, deducible_pct: prorrata }])
      .map((l) => ({ concepto: l.concepto || '', base: l.base ?? '', tipo_iva: l.tipo_iva ?? 21, deducible_pct: l.deducible_pct ?? prorrata })),
    deducible: base.deducible ?? true,
    retencion_tipo: base.retencion_tipo || 'NINGUNA',
    retencion_pct: base.retencion_pct || '',
    fecha_pago: base.fecha_pago || '',
    forma_pago: base.forma_pago || '',
    adjunto: lectura?.adjunto || gasto?.adjunto || null,
  }));
  const [guardando, setGuardando] = useState(false);
  const [repetido, setRepetido] = useState(null);
  const [sugerencias, setSugerencias] = useState([]);
  // El nombre que ya viene puesto (de la lectura, del gasto o elegido de la
  // lista) no vuelve a abrir las sugerencias.
  const nombreFijado = useRef(base.proveedor?.nombre || '');
  const calc = useMemo(() => calcularGasto(form.lineas, form.retencion_tipo, form.retencion_pct), [form.lineas, form.retencion_tipo, form.retencion_pct]);
  const setLinea = (i, c) => setForm((f) => ({ ...f, lineas: f.lineas.map((l, j) => (j === i ? { ...l, ...c } : l)) }));
  const ayuda = categorias.find((c) => c.codigo === form.categoria)?.ayuda;

  useEffect(() => {
    const t = form.proveedor.nombre;
    if (!t || t.length < 2 || t === nombreFijado.current) { setSugerencias([]); return undefined; }
    const h = setTimeout(() => {
      api.get('/api/facturacion/proveedores', { params: { q: t } }).then((r) => setSugerencias(r.data || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(h);
  }, [form.proveedor.nombre]);

  const usarProveedor = (p) => {
    nombreFijado.current = p.nombre;
    setForm((f) => ({
      ...f, proveedor: { nombre: p.nombre, nif: p.nif || '' }, categoria: p.categoria || f.categoria,
      retencion_tipo: p.retencion_tipo || f.retencion_tipo, retencion_pct: p.retencion_pct || f.retencion_pct,
    }));
    setSugerencias([]);
  };

  const guardar = async (forzar = false) => {
    setGuardando(true);
    const cuerpo = {
      proveedor: { nombre: form.proveedor.nombre.trim(), nif: form.proveedor.nif.trim() },
      numero_factura: form.numero_factura, fecha: form.fecha, fecha_recepcion: form.fecha_recepcion || null,
      categoria: form.categoria, concepto: form.concepto,
      lineas: form.lineas.map((l) => ({ concepto: l.concepto, base: num(l.base), tipo_iva: num(l.tipo_iva), deducible_pct: l.deducible_pct === '' ? 100 : num(l.deducible_pct) })),
      deducible: form.deducible, retencion_tipo: form.retencion_tipo,
      retencion_pct: form.retencion_tipo === 'NINGUNA' ? null : num(form.retencion_pct),
      fecha_pago: form.fecha_pago || null, forma_pago: form.forma_pago || null,
      adjunto: form.adjunto ? { id: form.adjunto.id, nombre: form.adjunto.nombre || '' } : null,
      forzar,
    };
    try {
      if (gasto?.id) await api.put(`/api/facturacion/gastos/${gasto.id}`, cuerpo);
      else await api.post('/api/facturacion/gastos', cuerpo);
      toast.success(gasto?.id ? 'Gasto corregido' : 'Gasto apuntado');
      onGuardado();
    } catch (e) {
      if (e?.response?.status === 409) setRepetido(e.response.data?.detail || 'Parece repetido');
      else toast.error(mensajeError(e, 'No se ha podido guardar el gasto'));
    } finally { setGuardando(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="editor-gasto">
        <DialogHeader><DialogTitle>{gasto?.id ? 'Corregir gasto' : 'Nuevo gasto'}</DialogTitle></DialogHeader>
        {lectura && (
          <div className={`rounded-lg border p-3 text-sm ${lectura.avisos?.length || lectura.confianza !== 'alta' ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-emerald-300 bg-emerald-50 text-emerald-900'}`} data-testid="gasto-lectura">
            <div className="font-medium flex items-center gap-1"><Sparkles className="w-4 h-4" /> Leída con IA (confianza {lectura.confianza}) · revísala con el papel delante</div>
            {lectura.total_impreso != null && <div>Total impreso en el documento: <b>{formatEUR(lectura.total_impreso)}</b></div>}
            {lectura.categoria_sugerida_por === 'proveedor' && <div>Categoría: la del último gasto de este proveedor.</div>}
            {lectura.categoria_sugerida_por === 'palabras' && <div>Categoría sugerida por el concepto: confírmala.</div>}
            {(lectura.avisos || []).map((a) => <div key={a} className="flex gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{a}</div>)}
          </div>
        )}
        <div className="grid md:grid-cols-6 gap-3">
          <div className="md:col-span-3 relative">
            <Label>Proveedor</Label>
            <Input value={form.proveedor.nombre} onChange={(e) => setForm((f) => ({ ...f, proveedor: { ...f.proveedor, nombre: e.target.value } }))} data-testid="gasto-proveedor" />
            {sugerencias.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-background shadow-md">
                {sugerencias.slice(0, 6).map((p) => (
                  <button type="button" key={`${p.nif}-${p.nombre}`} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-muted" onClick={() => usarProveedor(p)}>
                    {p.nombre} <span className="text-muted-foreground">{p.nif}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-1"><Label>NIF</Label><Input value={form.proveedor.nif} onChange={(e) => setForm((f) => ({ ...f, proveedor: { ...f.proveedor, nif: e.target.value.toUpperCase() } }))} /></div>
          <div className="md:col-span-2"><Label>Nº de factura</Label><Input value={form.numero_factura} onChange={(e) => setForm((f) => ({ ...f, numero_factura: e.target.value }))} /></div>
          <div className="md:col-span-2"><Label>Fecha de la factura</Label><Input type="date" value={form.fecha} onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))} /></div>
          <div className="md:col-span-2"><Label>Recibida el (si es otro día)</Label><Input type="date" value={form.fecha_recepcion} onChange={(e) => setForm((f) => ({ ...f, fecha_recepcion: e.target.value }))} /></div>
          <div className="md:col-span-2">
            <Label>Categoría</Label>
            <select className={SELECT} value={form.categoria} onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))} data-testid="gasto-categoria">
              {categorias.map((c) => <option key={c.codigo} value={c.codigo}>{c.nombre} ({c.cuenta})</option>)}
            </select>
          </div>
          {ayuda && <p className="md:col-span-6 -mt-2 text-xs text-muted-foreground">{ayuda}</p>}
          <div className="md:col-span-6"><Label>Concepto</Label><Input value={form.concepto} onChange={(e) => setForm((f) => ({ ...f, concepto: e.target.value }))} /></div>
        </div>
        <div>
          <Label>Bases e IVA</Label>
          <div className="space-y-2 mt-1">
            {form.lineas.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <div className="col-span-12 md:col-span-5"><Input placeholder="Descripción (opcional)" value={l.concepto} onChange={(e) => setLinea(i, { concepto: e.target.value })} /></div>
                <div className="col-span-4 md:col-span-2"><Input aria-label="Base" placeholder="Base €" value={l.base} onChange={(e) => setLinea(i, { base: e.target.value })} data-testid={`gasto-base-${i}`} /></div>
                <div className="col-span-4 md:col-span-2">
                  <select className={SELECT} aria-label="Tipo de IVA" value={String(l.tipo_iva)} onChange={(e) => setLinea(i, { tipo_iva: Number(e.target.value) })}>
                    {[21, 10, 4, 0].map((t) => <option key={t} value={t}>IVA {t} %</option>)}
                  </select>
                </div>
                <div className="col-span-3 md:col-span-2"><Input aria-label="% deducible" title="% del IVA que se deduce (prorrata)" value={l.deducible_pct} onChange={(e) => setLinea(i, { deducible_pct: e.target.value })} /></div>
                <div className="col-span-1 flex justify-end"><Button variant="ghost" size="icon" onClick={() => setForm((f) => ({ ...f, lineas: f.lineas.filter((_, j) => j !== i) }))} disabled={form.lineas.length === 1} aria-label="Quitar"><Trash2 className="w-4 h-4" /></Button></div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2">
            <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, lineas: [...f.lineas, { concepto: '', base: '', tipo_iva: 21, deducible_pct: prorrata }] }))}><Plus className="w-4 h-4 mr-1" /> Otro tipo de IVA</Button>
            <span className="text-xs text-muted-foreground">La última columna es el % del IVA que se deduce (prorrata: {prorrata} %)</span>
          </div>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label>Retención de IRPF</Label>
            <select className={SELECT} value={form.retencion_tipo}
              onChange={(e) => { const r = retenciones.find((x) => x.codigo === e.target.value); setForm((f) => ({ ...f, retencion_tipo: e.target.value, retencion_pct: f.retencion_pct || r?.habitual || '' })); }}>
              {retenciones.map((r) => <option key={r.codigo} value={r.codigo}>{r.nombre}</option>)}
            </select>
          </div>
          <div><Label>% retención</Label><Input value={form.retencion_pct} disabled={form.retencion_tipo === 'NINGUNA'} onChange={(e) => setForm((f) => ({ ...f, retencion_pct: e.target.value }))} /></div>
          <label className="flex items-end gap-2 text-sm pb-2"><input type="checkbox" checked={form.deducible} onChange={(e) => setForm((f) => ({ ...f, deducible: e.target.checked }))} /> IVA deducible</label>
          <div className="md:col-span-2"><Label>Pagada el</Label><Input type="date" value={form.fecha_pago} onChange={(e) => setForm((f) => ({ ...f, fecha_pago: e.target.value }))} /></div>
          <div className="md:col-span-2">
            <Label>Forma de pago</Label>
            <select className={SELECT} value={form.forma_pago} onChange={(e) => setForm((f) => ({ ...f, forma_pago: e.target.value }))}>
              <option value="">—</option>{(ajustes?.catalogos?.formas_pago || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="rounded-lg border border-border p-3 text-sm grid grid-cols-2 md:grid-cols-5 gap-2">
          <div>Base <div className="font-semibold tabular-nums">{formatEUR(calc.base)}</div></div>
          <div>IVA <div className="font-semibold tabular-nums">{formatEUR(calc.cuota_iva)}</div></div>
          <div>IVA deducible <div className="font-semibold tabular-nums">{formatEUR(form.deducible ? calc.cuota_deducible : 0)}</div></div>
          <div>Retención <div className="font-semibold tabular-nums">{formatEUR(calc.retencion)}</div></div>
          <div>Total <div className="font-semibold tabular-nums text-[#13231E]" data-testid="gasto-total">{formatEUR(calc.total)}</div></div>
        </div>
        {form.adjunto && (
          <button type="button" className="text-sm text-[#2E6E5E] underline flex items-center gap-1" onClick={() => abrirBlob(`/api/facturacion/adjuntos/${form.adjunto.id}`).catch(() => toast.error('No se ha podido abrir el documento'))}>
            <Paperclip className="w-4 h-4" /> {form.adjunto.nombre || 'Documento adjunto'}
          </button>
        )}
        {repetido && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            {repetido}
            <div className="mt-2"><Button size="sm" onClick={() => { setRepetido(null); guardar(true); }}>Guardar igualmente</Button></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
          <Button onClick={() => guardar(false)} disabled={guardando || !form.proveedor.nombre.trim() || !form.fecha} data-testid="gasto-guardar">
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar gasto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TabGastos({ anio, trimestre, ajustes }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [categoria, setCategoria] = useState('');
  const [pendientes, setPendientes] = useState(false);
  const [editor, setEditor] = useState(null);       // { gasto?, lectura? }
  const [leyendo, setLeyendo] = useState(false);
  const [borrando, setBorrando] = useState(null);
  const fichero = useRef(null);
  const categorias = ajustes?.catalogos?.categorias || [];

  const cargar = useCallback(() => {
    api.get('/api/facturacion/gastos', { params: paramsDe(anio, trimestre, { ...(q ? { q } : {}), ...(categoria ? { categoria } : {}), ...(pendientes ? { pendientes: true } : {}) }) })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { setItems([]); toast.error(mensajeError(e, 'No se han podido leer los gastos')); });
  }, [anio, trimestre, q, categoria, pendientes]);
  useEffect(() => { cargar(); }, [cargar]);

  const subir = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setLeyendo(true);
    try {
      const datos = new FormData();
      datos.append('archivo', f);
      const r = await api.post('/api/facturacion/gastos/leer', datos);
      if (r.data?.lectura) setEditor({ lectura: { ...r.data.lectura, adjunto: r.data.adjunto } });
      else {
        toast.message(r.data?.aviso || 'No se ha podido leer: apúntalo a mano');
        setEditor({ gasto: { adjunto: r.data?.adjunto } });
      }
    } catch (err) { toast.error(mensajeError(err, 'No se ha podido subir el documento')); }
    finally { setLeyendo(false); }
  };

  const borrar = async () => {
    try { await api.delete(`/api/facturacion/gastos/${borrando.id}`); toast.success('Gasto eliminado'); setBorrando(null); cargar(); }
    catch (e) { toast.error(mensajeError(e, 'No se ha podido borrar')); }
  };

  const nombreCat = (c) => categorias.find((x) => x.codigo === c)?.nombre || c;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => fichero.current?.click()} disabled={leyendo} data-testid="gasto-subir">
            {leyendo ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
            Subir factura (se lee con IA)
          </Button>
          <input ref={fichero} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={subir} />
          <Button variant="outline" onClick={() => setEditor({})} data-testid="gasto-nuevo"><Plus className="w-4 h-4 mr-1" /> Apuntar a mano</Button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select className={`${SELECT} w-48`} value={categoria} onChange={(e) => setCategoria(e.target.value)} aria-label="Categoría">
            <option value="">Todas las categorías</option>{categorias.map((c) => <option key={c.codigo} value={c.codigo}>{c.nombre}</option>)}
          </select>
          <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={pendientes} onChange={(e) => setPendientes(e.target.checked)} /> Sin pagar</label>
          <Input className="w-48" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      {items === null ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        : items.length === 0 ? (
          <EmptyState icon={Receipt} title="Sin gastos en el periodo" description="Sube el PDF o la foto de una factura de gasto y se rellena sola, o apúntala a mano." />
        ) : (
          <div className="cp-card overflow-hidden"><div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tabla-gastos">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr><th className={TH}>Fecha</th><th className={TH}>Proveedor</th><th className={TH}>Nº</th><th className={TH}>Categoría</th>
                  <th className={`${TH} text-right`}>Base</th><th className={`${TH} text-right`}>IVA</th><th className={`${TH} text-right`}>Retención</th>
                  <th className={`${TH} text-right`}>Total</th><th className={TH}>Pago</th><th className={`${TH} text-right`} /></tr>
              </thead>
              <tbody>
                {items.map((g) => (
                  <tr key={g.id} className="border-t border-border">
                    <td className={`${TD} whitespace-nowrap`}>{formatDate(g.fecha)}</td>
                    <td className={TD}>{g.proveedor?.nombre}<div className="text-[11px] text-muted-foreground">{g.proveedor?.nif}</div></td>
                    <td className={TD}>{g.numero_factura || '—'}</td>
                    <td className={TD}>{nombreCat(g.categoria)}</td>
                    <td className={`${TD} text-right tabular-nums`}>{formatEUR(g.base)}</td>
                    <td className={`${TD} text-right tabular-nums`}>{formatEUR(g.cuota_iva)}</td>
                    <td className={`${TD} text-right tabular-nums`}>{g.retencion ? formatEUR(g.retencion) : '—'}</td>
                    <td className={`${TD} text-right tabular-nums font-medium`}>{formatEUR(g.total)}</td>
                    <td className={TD}>{g.pagado ? <Etiqueta tono="verde">Pagada {formatDate(g.fecha_pago)}</Etiqueta> : <Etiqueta tono="ambar">Pendiente</Etiqueta>}</td>
                    <td className={`${TD} text-right whitespace-nowrap`}>
                      {g.adjunto && <Button variant="ghost" size="icon" title="Documento" aria-label="Documento" onClick={() => abrirBlob(`/api/facturacion/adjuntos/${g.adjunto.id}`).catch(() => toast.error('No se ha podido abrir'))}><Paperclip className="w-4 h-4" /></Button>}
                      <Button variant="ghost" size="icon" onClick={() => setEditor({ gasto: g })} title="Corregir" aria-label="Corregir"><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setBorrando(g)} title="Borrar" aria-label="Borrar"><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div></div>
        )}
      {editor && <EditorGasto gasto={editor.gasto} lectura={editor.lectura} ajustes={ajustes} onClose={() => setEditor(null)} onGuardado={() => { setEditor(null); cargar(); }} />}
      {borrando && (
        <Dialog open onOpenChange={(v) => !v && setBorrando(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="text-destructive">¿Borrar este gasto?</DialogTitle></DialogHeader>
            <p className="text-sm">{borrando.proveedor?.nombre} · {borrando.numero_factura || 's/n'} · {formatEUR(borrando.total)}. Queda escrito en el historial quién lo borró.</p>
            <DialogFooter><Button variant="outline" onClick={() => setBorrando(null)}>Cancelar</Button><Button variant="destructive" onClick={borrar}>Borrar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ===========================================================================
// AJUSTES
// ===========================================================================
function TabAjustes({ ajustes, onGuardado }) {
  const [form, setForm] = useState(() => ({
    ...(ajustes?.emisor || {}), prorrata_pct: ajustes?.prorrata_pct ?? 100,
    mencion_exencion: ajustes?.mencion_exencion || '', pie_factura: ajustes?.pie_factura || '',
  }));
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await api.put('/api/facturacion/ajustes', { ...form, prorrata_pct: num(form.prorrata_pct) });
      toast.success('Datos de facturación guardados');
      onGuardado(r.data);
    } catch (e) { toast.error(mensajeError(e, 'No se han podido guardar')); }
    finally { setGuardando(false); }
  };
  const campo = (k, etiqueta, extra = {}) => (
    <div className={extra.className || ''}><Label>{etiqueta}</Label><Input value={form[k] || ''} onChange={(e) => set(k, e.target.value)} {...(extra.input || {})} /></div>
  );
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <SectionCard title="Datos fiscales de la sociedad" className="lg:col-span-2">
        <p className="text-sm text-muted-foreground mb-3">Salen en cada factura. Las ya emitidas guardan los del día en que se emitieron.</p>
        <div className="grid md:grid-cols-6 gap-3" data-testid="facturacion-ajustes">
          {campo('razon_social', 'Razón social', { className: 'md:col-span-4', input: { 'data-testid': 'ajustes-razon' } })}
          {campo('nif', 'NIF / CIF', { className: 'md:col-span-2', input: { 'data-testid': 'ajustes-nif' } })}
          {campo('domicilio', 'Domicilio fiscal', { className: 'md:col-span-6' })}
          {campo('cp', 'C.P.', { className: 'md:col-span-1' })}
          {campo('municipio', 'Municipio', { className: 'md:col-span-3' })}
          {campo('provincia', 'Provincia', { className: 'md:col-span-2' })}
          {campo('datos_registrales', 'Datos registrales (Registro Mercantil)', { className: 'md:col-span-6' })}
          {campo('iban', 'IBAN para transferencias', { className: 'md:col-span-6' })}
        </div>
      </SectionCard>
      <div className="space-y-4">
        <SectionCard title="IVA y textos">
          <div className="space-y-3">
            <div>
              <Label>Prorrata provisional (% del IVA de los gastos que se deduce)</Label>
              <Input value={form.prorrata_pct} onChange={(e) => set('prorrata_pct', e.target.value)} />
              <p className="text-xs text-muted-foreground mt-1">Una clínica que mezcla servicios exentos (sanidad) y gravados (estética, óptica) solo deduce una parte del IVA. El Resumen estima el % con lo facturado; confírmalo con la gestoría.</p>
            </div>
            <div><Label>Mención de la exención sanitaria</Label><Textarea rows={3} value={form.mencion_exencion} onChange={(e) => set('mencion_exencion', e.target.value)} /></div>
            <div><Label>Pie de factura (opcional)</Label><Textarea rows={2} value={form.pie_factura} onChange={(e) => set('pie_factura', e.target.value)} /></div>
          </div>
        </SectionCard>
        <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-xs text-sky-950">
          <b>VERI*FACTU.</b> Esta facturación aún no envía registros a la AEAT. Para una SL el reglamento de los sistemas de facturación
          obliga desde el 1 de enero de 2027: antes de esa fecha, la emisión se conectará a un sistema VERI*FACTU.
        </div>
        <Button className="w-full" onClick={guardar} disabled={guardando} data-testid="ajustes-guardar">
          {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Settings2 className="w-4 h-4 mr-2" /> Guardar</>}
        </Button>
      </div>
    </div>
  );
}

// ===========================================================================
export default function FacturacionPage() {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [trimestre, setTrimestre] = useState(trimestreDeHoy());
  const [ajustes, setAjustes] = useState(null);
  const [pestana, setPestana] = useState('resumen');

  useEffect(() => {
    api.get('/api/facturacion/ajustes').then((r) => setAjustes(r.data))
      .catch((e) => toast.error(mensajeError(e, 'No se han podido leer los ajustes de facturación')));
  }, []);

  return (
    <div>
      <PageHeader title="Facturación" subtitle="Facturas emitidas, gastos e impuestos del trimestre de la sociedad."
        actions={<Periodo anio={anio} setAnio={setAnio} trimestre={trimestre} setTrimestre={setTrimestre} />} />
      {ajustes?.faltan?.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2" data-testid="facturacion-faltan">
          <span className="flex gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />Para emitir facturas faltan los datos fiscales de la sociedad: {ajustes.faltan.join(', ')}.</span>
          <Button size="sm" variant="outline" onClick={() => setPestana('ajustes')}>Completar</Button>
        </div>
      )}
      <Tabs value={pestana} onValueChange={setPestana}>
        <TabsList>
          <TabsTrigger value="resumen" data-testid="tab-resumen"><Landmark className="w-4 h-4 mr-1" /> Resumen</TabsTrigger>
          <TabsTrigger value="facturas" data-testid="tab-facturas"><FileText className="w-4 h-4 mr-1" /> Facturas</TabsTrigger>
          <TabsTrigger value="gastos" data-testid="tab-gastos"><Receipt className="w-4 h-4 mr-1" /> Gastos</TabsTrigger>
          <TabsTrigger value="ajustes" data-testid="tab-ajustes"><Settings2 className="w-4 h-4 mr-1" /> Ajustes</TabsTrigger>
        </TabsList>
        <TabsContent value="resumen" className="mt-4"><TabResumen anio={anio} trimestre={trimestre} /></TabsContent>
        <TabsContent value="facturas" className="mt-4"><TabFacturas anio={anio} trimestre={trimestre} ajustes={ajustes} /></TabsContent>
        <TabsContent value="gastos" className="mt-4"><TabGastos anio={anio} trimestre={trimestre} ajustes={ajustes} /></TabsContent>
        <TabsContent value="ajustes" className="mt-4">
          {ajustes ? <TabAjustes ajustes={ajustes} onGuardado={setAjustes} /> : <Loader2 className="w-5 h-5 animate-spin" />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
